import type { AcousticRoomSignal } from '../../lib/liveLessonTypes';
import { loudnessNormalizedFromDb } from '../../lib/acoustic/loudnessScale';
import { Button } from '../ui';
import CollapsibleTile from './CollapsibleTile';

type RoomEnergyWidgetProps = {
  signal: AcousticRoomSignal | null;
  history: AcousticRoomSignal[];
  audioEnabled: boolean;
  isLoadingModels?: boolean;
  onToggleAudio: () => void;
};

type RoomBand = 'quiet' | 'balanced' | 'lively';

function mapBand(signal: AcousticRoomSignal | null): RoomBand {
  if (!signal) return 'quiet';
  if (signal.phaseFit === 'high') return 'lively';
  if (signal.phaseFit === 'ok') return 'balanced';
  return 'quiet';
}

const BAND_LABELS: Record<RoomBand, string> = {
  quiet: 'leise',
  balanced: 'ausgeglichen',
  lively: 'lebhaft'
};

function situationLabel(signal: AcousticRoomSignal | null): string {
  switch (signal?.detectedSituation) {
    case 'single_speaker':
      return 'Einzelsprecher';
    case 'conversation':
      return 'Dialog';
    case 'babble':
      return 'Gemurmel';
    case 'shouting':
      return 'Geschrei';
    case 'silence':
      return 'Stille';
    default:
      return 'Unklar';
  }
}

function buildRoomSummary(signal: AcousticRoomSignal | null): string {
  if (!signal) return '—';
  const band = BAND_LABELS[mapBand(signal)];
  const situation = situationLabel(signal);
  const confidence = Math.round((signal.situationConfidence || 0) * 100);
  if (signal.detectedSituation === 'silence') {
    return `${situation} · ${band}`;
  }
  return `${situation} · ${band} (${confidence}%)`;
}

function createSparklinePath(points: AcousticRoomSignal[]): string {
  if (!points.length) return '';
  const width = 280;
  const height = 44;
  return points
    .map((entry, index) => {
      const x = (index / Math.max(1, points.length - 1)) * width;
      const normalized = loudnessNormalizedFromDb(entry.levelDb);
      const y = height - normalized * height;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export default function RoomEnergyWidget({
  signal,
  history,
  audioEnabled,
  isLoadingModels = false,
  onToggleAudio
}: RoomEnergyWidgetProps) {
  const sparklinePath = createSparklinePath(history);
  const roomSummary = buildRoomSummary(signal);

  return (
    <CollapsibleTile
      title="Raumenergie"
      defaultOpen={false}
      contentClassName="space-y-2"
      headerActions={
        <Button size="sm" variant={audioEnabled ? 'primary' : 'outline'} onClick={onToggleAudio}>
          Audio {audioEnabled ? 'an' : 'aus'}
        </Button>
      }
    >
        {isLoadingModels ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Modelle werden geladen …</p>
        ) : null}
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <span className="text-gray-500 dark:text-gray-400">Raumlage</span>{' '}
          <span className="font-semibold">{roomSummary}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Lautstärke</span>
          <span>
            {signal?.levelDb?.toFixed(1) ?? '—'} dB {signal?.trendLong ? `(${signal.trendLong})` : ''}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
          <div>
            Volatilität kurz: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.volatilityShort?.toFixed(2) ?? '—'}</span>
          </div>
          <div>
            Volatilität lang: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.volatilityLong?.toFixed(2) ?? '—'}</span>
          </div>
          <div>
            Trend kurz: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.trendShort ?? '—'}</span>
          </div>
          <div>
            Trend lang: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.trendLong ?? '—'}</span>
          </div>
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50 p-1.5 dark:border-gray-700 dark:bg-gray-900/40">
          <svg viewBox="0 0 280 44" className="h-10 w-full" aria-label="Lautstärke-Verlauf">
            <path d={sparklinePath} fill="none" stroke="currentColor" strokeWidth="1.8" className="text-primary-600" />
          </svg>
        </div>
        <div className="space-y-1">
          {(signal?.bandMetrics || []).map((bandEntry) => (
            <div key={bandEntry.key} className="grid grid-cols-[82px_1fr_auto_auto] items-center gap-2 text-xs" title={bandEntry.meaning}>
              <span className="text-gray-500 dark:text-gray-400">{bandEntry.label}</span>
              <BandLevelBar loudness={bandEntry.loudness} />
              <span className="text-gray-500 dark:text-gray-400">{bandEntry.volatility.toFixed(2)}</span>
              <span className="text-gray-600 dark:text-gray-300">{bandEntry.trend}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Änderungsscore: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.changeScore?.toFixed(2) ?? '—'}</span>
        </div>
        <RhythmRow signal={signal} />
        <GlobalFeatureGrid signal={signal} />
        {signal?.competingSituations?.length ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Konkurrenz:{' '}
            {signal.competingSituations
              .map((entry) => `${entry.key.replace(/_/g, ' ')} ${Math.round(entry.score * 100)}%`)
              .join(' · ')}
          </div>
        ) : null}
        {signal?.bandActivity?.length ? (
          <div className="space-y-1">
            {signal.bandActivity.slice(0, 3).map((entry) => (
              <div key={entry.band} className="text-xs text-gray-500 dark:text-gray-400">
                {entry.band}: Persistenz <span className="font-medium text-gray-700 dark:text-gray-300">{entry.persistence.toFixed(2)}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="space-y-1">
          {(signal?.situationScores || []).map((entry) => (
            <ScoreRow key={entry.key} label={entry.label} score={entry.score} />
          ))}
        </div>
    </CollapsibleTile>
  );
}

function BandLevelBar({ loudness }: { loudness: number }) {
  return (
    <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
      <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${Math.round(loudness * 100)}%` }} />
    </div>
  );
}

function RhythmRow({ signal }: { signal: AcousticRoomSignal | null }) {
  return (
    <div className="text-xs text-gray-500 dark:text-gray-400">
      Rhythmus: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.rhythmRegularity?.toFixed(2) ?? '—'}</span> ·
      Spektralstabilität:{' '}
      <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.spectralStability?.toFixed(2) ?? '—'}</span>
    </div>
  );
}

function GlobalFeatureGrid({ signal }: { signal: AcousticRoomSignal | null }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
      <div>
        Dominanz: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.globalFeatures?.dominance?.toFixed(2) ?? '—'}</span>
      </div>
      <div>
        Diffusität: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.globalFeatures?.diffuseness?.toFixed(2) ?? '—'}</span>
      </div>
      <div>
        Entropie: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.globalFeatures?.entropy?.toFixed(2) ?? '—'}</span>
      </div>
      <div>
        Übergang: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.transitionLikelihood?.toFixed(2) ?? '—'}</span>
      </div>
      <div>
        Pause-Pulse: <span className="font-medium text-gray-700 dark:text-gray-300">{signal?.globalFeatures?.pausePulse?.toFixed(2) ?? '—'}</span>
      </div>
    </div>
  );
}

function ScoreRow({ label, score }: { label: string; score: number }) {
  return (
    <div className="grid grid-cols-[120px_1fr_auto] items-center gap-2 text-xs">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-1.5 rounded-full bg-gray-500" style={{ width: `${Math.round(score * 100)}%` }} />
      </div>
      <span className="text-gray-600 dark:text-gray-300">{Math.round(score * 100)}%</span>
    </div>
  );
}
